import React from "react";
import heroBackground from "../assets/bg.jpg";
import doorBackground from "../assets/door.jpg";
import windowBackground from "../assets/window.jpg";

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Premium Windows
            <span className="block text-amber-400">& Doors</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
            Crafting exceptional windows and doors that transform spaces with
            elegance, security, and energy efficiency
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-4 rounded-lg transition duration-300 transform hover:scale-105">
              Get Free Quote
            </button>
          </div>
        </div>
        {/* Animated background elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-amber-400/10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-32 left-20 w-24 h-24 bg-blue-400/10 rounded-full animate-pulse delay-700"></div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Why Choose SM Constructions?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With years of expertise, we deliver superior quality windows and
              doors that exceed expectations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group p-8 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition duration-300">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                Premium Quality
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Using only the finest materials and advanced manufacturing
                techniques to ensure durability and beauty that lasts.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition duration-300">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                Custom Design
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Tailored solutions designed to match your unique style and
                architectural requirements perfectly.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl hover:shadow-xl transition duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition duration-300">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                Energy Efficient
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Advanced insulation technology that reduces energy costs and
                creates a comfortable living environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Our Specialties
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive solutions for all your window and door needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
            {/* Windows */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition duration-300">
              <div
                className="h-64 bg-gradient-to-br from-blue-400 to-blue-600"
                style={{
                  backgroundImage: `url(${windowBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              ></div>
              <div className="p-8">
                <h3 className="text-3xl font-bold text-slate-800 mb-4">
                  Premium Windows
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  From casement to bay windows, our precision-engineered
                  solutions combine beauty with performance. Double and
                  triple-pane options with advanced coatings for maximum
                  efficiency.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Casement & Awning Windows
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Bay & Bow Windows
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Picture & Specialty Windows
                  </li>
                </ul>
              </div>
            </div>

            {/* Doors */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition duration-300">
              <div
                className="h-64 bg-gradient-to-br from-amber-400 to-orange-500"
                style={{
                  backgroundImage: `url(${doorBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              ></div>
              <div className="p-8">
                <h3 className="text-3xl font-bold text-slate-800 mb-4">
                  Designer Doors
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Entry doors that make a statement. From classic to
                  contemporary designs, crafted with premium materials for
                  security, beauty, and lasting value.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                    Entry & Front Doors
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                    Patio & French Doors
                  </li>
                  <li className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                    Interior & Sliding Doors
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600">
              From consultation to installation, we make it seamless
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Consultation
              </h3>
              <p className="text-gray-600">
                Free in-home consultation to understand your needs and vision
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Design</h3>
              <p className="text-gray-600">
                Custom design and detailed measurements for perfect fit
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Manufacturing
              </h3>
              <p className="text-gray-600">
                Precision crafting with premium materials and quality control
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Installation
              </h3>
              <p className="text-gray-600">
                Professional installation with attention to every detail
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Get your free consultation today and discover how our premium
            windows and doors can enhance your home
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-10 py-4 rounded-lg transition duration-300 transform hover:scale-105">
              Schedule Free Consultation
            </button>
            <button className="border-2 border-white hover:bg-white hover:text-slate-900 text-white font-semibold px-10 py-4 rounded-lg transition duration-300">
              Call (289) 200-3498
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
